import { Component, computed, inject, ChangeDetectionStrategy } from '@angular/core';
import { UtilityService } from '@eforall/common';
import { GridContentWidget, PageTabsMainTabGridSize } from '@eforall/dashboard';
import { CourseEnrollmentFuncData } from '@interfaces';
import { AcceleratorAreaService } from '../../../accelerator-area.service';
import { AccDeliveryService } from '../../acc-delivery.service';
import { getSetup } from './grid/get-setup';

export interface CourseEnrollment extends CourseEnrollmentFuncData {
	accVentureId: number,
	ventureName: string,
	isVentureDroppedOut: boolean,
	ventureMaxCompletionPercent: number,
}

@Component({
	selector: 'acc-enrollments-view-part',
	standalone: true,
	imports: [GridContentWidget],
	changeDetection: ChangeDetectionStrategy.Eager,
	templateUrl: './enrollments-view.part.html',
})
export class AccEnrollmentsViewPart {

	private readonly util = inject(UtilityService);
	private readonly pageService = inject(AccDeliveryService);
	private readonly areaService = inject(AcceleratorAreaService);

	public setup = getSetup(this.util);

	public pageTabsGridSize = computed<PageTabsMainTabGridSize | undefined>(() => {
		if (this.pageService.layout()) return { layout: this.pageService.layout()!, viewSelector: true };
		return undefined;
	});


	public enrollments = computed<CourseEnrollment[]>(() => {
		const data = this.areaService.data();
		if (!data) return [];

		const ventureMap = this.areaService.accVentureMap();

		const accEntPersonMap = this.util.array.toMap(data.entrepreneurs, e => e.personId);

		// Build venture max completion 
		// using map since multiple updates is possible, and map is better for that
		const enrollmentsByVenture = new Map<number, number>();

		const courseEnrollments = data.courseEnrollments.map(enrollment => {
			const entrepreneur = accEntPersonMap[enrollment.personId];
			if (!entrepreneur) return undefined;

			const accVenture = ventureMap[entrepreneur.accVentureId]!; // if entrepreneur exists, venture exists as entrepreneur list is fetched based on ventures
			const accVentureId = accVenture.accVentureId;

			// Track max completion
			const currentMax = enrollmentsByVenture.get(accVentureId) ?? 0;
			enrollmentsByVenture.set(accVentureId, Math.max(currentMax, enrollment.percentComplete));

			return {
				...enrollment,
				accVentureId: accVenture.accVentureId,
				ventureName: accVenture.ventureName,
				ventureMaxCompletionPercent: 0, // to be set later
				isVentureDroppedOut: accVenture.status === 'Never Started' || accVenture.status === 'Dropped Out',
			};
		}).filter(e => !!e);


		return courseEnrollments.map(enrollment => ({
			...enrollment,
			ventureMaxCompletionPercent: enrollmentsByVenture.get(enrollment.accVentureId) ?? 0,
		}));
	});
}